import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { PrintOrientation, RxVersion } from '@shared/models/enums/enums';
import { EnvironmentParams } from '@shared/models/environment-params';
import { FeatureToggle } from '@shared/models/feature-toggle';
import { IdName } from '@shared/models/id-name';
import { RoleTypeEnum } from '@shared/models/role-type';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { RxComponents } from '@shared/models/rx-models/interfaces/rx-components-model';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { UserSettings } from '@shared/models/user-settings';
import { ScannerVersion } from '@shared/models/company-scanner';
import { LinkedCompaniesBySendToType } from '@shared/models/linked-companies';
import type { Maybe } from '@/types/globals';
import { ViewModeEnum } from '@shared/models/consts';

export interface ShellState {
	dateFormat: string;
	languageCode: string;
	isLoading: boolean;
	isRxPending: boolean;
	isReadOnly: boolean;
	readOnlyRules: Maybe<RxComponents>;
	validationMode: Maybe<string>;
	rxConfiguration: Maybe<RxConfiguration>;
	userSettings: Maybe<UserSettings>;
	contactId: Maybe<number>;
	companyId: Maybe<number>;
	productType: Maybe<string>;
	applicationMode: Maybe<string>;
	/** @deprecated Used only in old Rx. Intuitive Rx calculates this in rx-intuitive-scan-options.facade.ts. */
	isNiriCaptureVisible: Maybe<boolean>;
	/** @deprecated Used only in old Rx. Intuitive Rx calculates this in rx-intuitive-scan-options.facade.ts. */
	isSleeveConfirmationVisible: Maybe<boolean>;
	isPreTreatmentVisibleForV0: Maybe<boolean>;
	/** @deprecated Used only in old Rx. Intuitive Rx calculates this in rx-intuitive-scan-options.facade.ts. */
	isPalatalFeedbackVisible: Maybe<boolean>;
	rx: Maybe<RxModel>;
	clonedRx: Maybe<RxModel>;
	clientVersion: Maybe<string>;
	packageVersion: Maybe<string>;
	scannerId: Maybe<string>;
	staticFilesEndpoint: string;
	featureToggles: Maybe<FeatureToggle[]>;
	apiEndpoint: Maybe<string>;
	rxAppApiEndpoint: Maybe<string>;
	patientAppApiEndpoint: Maybe<string>;
	doctorIdPatients: any;
	rxId: Maybe<string>;
	preparedNewRxId: Maybe<string>;
	orderId?: Maybe<number>;
	clonedFromRxId: Maybe<string>;
	isRxValidForSave: Maybe<boolean>;
	appSettings: EnvironmentParams;
	isRxTakenForScan: Maybe<boolean>;
	/** Host launch flag: a multilayer scan was added. */
	hasMultipleScanLayers: Maybe<boolean>;
	preTreatmentScanAttempted: boolean;
	authToken: Maybe<string>;
	rxsForPrint: Maybe<RxModel[]>;
	userRole: RoleTypeEnum;
	rxVersionFlow: RxVersion;
	forceV0: Maybe<boolean>;
	printOrientation: Maybe<PrintOrientation>;
	isHeadlessPrint: boolean;
	isPatientAppDialogOpen: boolean;
	isReturn: boolean;
	isResendScan: boolean;
	shouldAnonymizeRx: boolean;
	enableAllCaseTypesForAddRx: boolean;
	autoDisplayPrintViewInTheDOM?: boolean;
	isProcedureChanged: boolean;
	user: Maybe<IdName>;
	printStyleName: Maybe<string>;
	scannersVersions: Maybe<ScannerVersion[]>;
	isRxAttachmentsReady: boolean;
	rxSessionId: Maybe<string>;
	appBISessionId?: string;
	region: Maybe<string>;
	linkedCompanies: Maybe<LinkedCompaniesBySendToType>;
	viewMode?: ViewModeEnum;
	flowRxStep?: string;
	patientGuidProvided: boolean;
	isOnline: boolean | null;
	labBrokerSendStepBypassed: boolean;
}

export const getInitialState = (): ShellState => {
	return {
		dateFormat: 'MM/DD/YYYY',
		languageCode: 'en-US',
		isLoading: false,
		isRxPending: true,
		isReadOnly: false,
		validationMode: null,
		rxConfiguration: null,
		userSettings: null,
		contactId: null,
		companyId: null,
		productType: null,
		applicationMode: null,
		isNiriCaptureVisible: null,
		isSleeveConfirmationVisible: null,
		isPreTreatmentVisibleForV0: null,
		isPalatalFeedbackVisible: null,
		rx: null,
		clonedRx: null,
		clientVersion: null,
		packageVersion: null,
		scannerId: null,
		staticFilesEndpoint: '.',
		featureToggles: null,
		apiEndpoint: null,
		rxAppApiEndpoint: null,
		patientAppApiEndpoint: null,
		doctorIdPatients: null,
		rxId: null,
		preparedNewRxId: null,
		clonedFromRxId: null,
		isRxValidForSave: null,
		appSettings: {
			patientAppUrl: null,
			rxAttachmentsApiUrl: null,
			biTimberUrl: null,
			isProduction: false,
			loggingEndpoint: '',
			logging: {
				url: null,
				appId: null,
				eventType: null,
				appenderTypes: [],
				level: null,
				requiredFields: [],
				maxDataPartLength: 7000
			},
			retryIntervals: [],
			linkToTheLearnNowForDesignSuite: null,
			pendoSdkUrl: '',
			remoteApplicationLoaderUrl: '',
			rxappBff: {
				urls: {
					us: null,
					eu: null,
					ap: null,
					cn: null
				}
			},
			signalFx: {
				realm: '',
				rumAuth: '',
				app: '',
				environment: '',
				isEnabled: false
			}
		},
		isRxTakenForScan: null,
		hasMultipleScanLayers: null,
		preTreatmentScanAttempted: false,
		orderId: null,
		authToken: null,
		readOnlyRules: null,
		rxsForPrint: null,
		userRole: RoleTypeEnum.Doctor,
		rxVersionFlow: RxVersion.CaseTypeFlow, // RxVersion.ProcedureFlow,
		forceV0: null,
		printOrientation: null,
		isHeadlessPrint: false,
		isPatientAppDialogOpen: false,
		isReturn: false,
		isResendScan: false,
		shouldAnonymizeRx: false,
		autoDisplayPrintViewInTheDOM: false,
		enableAllCaseTypesForAddRx: false,
		isProcedureChanged: false,
		user: null,
		printStyleName: null,
		scannersVersions: null,
		isRxAttachmentsReady: true,
		rxSessionId: null,
		region: null,
		linkedCompanies: null,
		viewMode: ViewModeEnum.ViewRx,
		patientGuidProvided: false,
		isOnline: null,
		labBrokerSendStepBypassed: false
	};
};

@Injectable({
	providedIn: 'root'
})
@StoreConfig({ name: 'Shell' })
export class ShellStore extends Store<ShellState> {
	constructor() {
		super(getInitialState());
	}

	updateProcedureChangedFlag(newValue: boolean) {
		this.update({ isProcedureChanged: newValue });
	}
}
